import {
  Component,
  lazy,
  Suspense,
  useEffect,
  type ComponentType,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { Link, Route, Routes, useLocation } from 'react-router';

import { RequireAuth } from '@/api/auth';
import { useTenantUsage } from '@/api/queries';
import { LoginScreen } from '@/screens/auth/LoginScreen';
import { AcceptInvitationScreen } from '@/screens/auth/AcceptInvitationScreen';
import { EmptyState } from '@/ui/primitives';

import { canOpenRoute } from './navigation';

// Каждый экран — отдельный маршрутный chunk. До этой границы все десять экранов,
// включая графики D3, попадали в initial bundle (553 КБ), хотя при входе нужен
// ровно один. Login остаётся eager: это первый экран без сессии.
const loadStationScreen = () =>
  import('@/screens/station/StationScreen').then((module) => ({ default: module.StationScreen }));
const loadToursScreen = () =>
  import('@/screens/tours/ToursScreen').then((module) => ({ default: module.ToursScreen }));
const loadCompareScreen = () =>
  import('@/screens/compare/CompareScreen').then((module) => ({ default: module.CompareScreen }));
const loadSharedShortlistScreen = () =>
  import('@/screens/compare/SharedShortlistScreen').then((module) => ({ default: module.SharedShortlistScreen }));
const loadStayScreen = () =>
  import('@/screens/stay/StayScreen').then((module) => ({ default: module.StayScreen }));
const loadFlightsScreen = () =>
  import('@/screens/flights/FlightsScreen').then((module) => ({ default: module.FlightsScreen }));
const loadDeparturesScreen = () =>
  import('@/screens/departures/DeparturesScreen').then((module) => ({
    default: module.DeparturesScreen,
  }));
const loadEventsScreen = () =>
  import('@/screens/events/EventsScreen').then((module) => ({ default: module.EventsScreen }));
const loadMarketScreen = () =>
  import('@/screens/market/MarketScreen').then((module) => ({ default: module.MarketScreen }));
const loadQualityScreen = () =>
  import('@/screens/quality/QualityScreen').then((module) => ({ default: module.QualityScreen }));
const loadOperatorsScreen = () =>
  import('@/screens/operators/OperatorsScreen').then((module) => ({
    default: module.OperatorsScreen,
  }));
const loadWatchScreen = () =>
  import('@/screens/watch/WatchScreen').then((module) => ({ default: module.WatchScreen }));
const loadSearchProfilesScreen = () =>
  import('@/screens/searchProfiles/SearchProfilesScreen').then((module) => ({
    default: module.SearchProfilesScreen,
  }));
const loadSignalRulesScreen = () =>
  import('@/screens/signalRules/SignalRulesScreen').then((module) => ({
    default: module.SignalRulesScreen,
  }));
const loadNotificationsScreen = () =>
  import('@/screens/notifications/NotificationsScreen').then((module) => ({
    default: module.NotificationsScreen,
  }));
const loadDecisionHistoryScreen = () =>
  import('@/screens/decisionHistory/DecisionHistoryScreen').then((module) => ({
    default: module.DecisionHistoryScreen,
  }));
const loadPagesScreen = () =>
  import('@/screens/pages/PagesScreen').then((module) => ({ default: module.PagesScreen }));
const loadDataScreen = () =>
  import('@/screens/data/DataScreen').then((module) => ({ default: module.DataScreen }));
const loadAccountProfileScreen = () =>
  import('@/screens/account/AccountProfileScreen').then((module) => ({
    default: module.AccountProfileScreen,
  }));
const loadAccountSecurityScreen = () =>
  import('@/screens/account/AccountSecurityScreen').then((module) => ({
    default: module.AccountSecurityScreen,
  }));
const loadWorkspaceScreen = () =>
  import('@/screens/workspace/WorkspaceScreen').then((module) => ({
    default: module.WorkspaceScreen,
  }));

const StationScreen = lazy(loadStationScreen);
const ToursScreen = lazy(loadToursScreen);
const CompareScreen = lazy(loadCompareScreen);
const SharedShortlistScreen = lazy(loadSharedShortlistScreen);
const StayScreen = lazy(loadStayScreen);
const FlightsScreen = lazy(loadFlightsScreen);
const DeparturesScreen = lazy(loadDeparturesScreen);
const EventsScreen = lazy(loadEventsScreen);
const MarketScreen = lazy(loadMarketScreen);
const QualityScreen = lazy(loadQualityScreen);
const OperatorsScreen = lazy(loadOperatorsScreen);
const WatchScreen = lazy(loadWatchScreen);
const SearchProfilesScreen = lazy(loadSearchProfilesScreen);
const SignalRulesScreen = lazy(loadSignalRulesScreen);
const NotificationsScreen = lazy(loadNotificationsScreen);
const DecisionHistoryScreen = lazy(loadDecisionHistoryScreen);
const PagesScreen = lazy(loadPagesScreen);
const DataScreen = lazy(loadDataScreen);
const AccountProfileScreen = lazy(loadAccountProfileScreen);
const AccountSecurityScreen = lazy(loadAccountSecurityScreen);
const WorkspaceScreen = lazy(loadWorkspaceScreen);

type RouteLoader = () => Promise<{ default: ComponentType }>;

/**
 * Начинает загрузку route chunk до завершения `/api/auth/me`. Импорт модуля
 * дедуплицируется браузером, поэтому React.lazy затем использует тот же chunk,
 * а не создаёт второй сетевой запрос.
 */
export function preloadAppRoute(target: string): Promise<unknown> | null {
  const pathname = target.split(/[?#]/, 1)[0] || '/';
  let loader: RouteLoader | undefined;
  if (pathname === '/') loader = loadStationScreen;
  else if (pathname === '/tours') loader = loadToursScreen;
  else if (pathname === '/compare') loader = loadCompareScreen;
  else if (pathname.startsWith('/shared/shortlist/')) loader = loadSharedShortlistScreen;
  else if (pathname.startsWith('/stay/')) loader = loadStayScreen;
  else if (pathname === '/flights') loader = loadFlightsScreen;
  else if (pathname === '/departures') loader = loadDeparturesScreen;
  else if (pathname === '/events') loader = loadEventsScreen;
  else if (pathname === '/market') loader = loadMarketScreen;
  else if (pathname === '/quality') loader = loadQualityScreen;
  else if (pathname === '/operators') loader = loadOperatorsScreen;
  else if (pathname === '/watch') loader = loadWatchScreen;
  else if (pathname === '/search-profiles') loader = loadSearchProfilesScreen;
  else if (pathname === '/signal-rules') loader = loadSignalRulesScreen;
  else if (pathname === '/notifications') loader = loadNotificationsScreen;
  else if (pathname === '/decision-history') loader = loadDecisionHistoryScreen;
  else if (pathname === '/pages') loader = loadPagesScreen;
  else if (pathname === '/data') loader = loadDataScreen;
  else if (pathname === '/account/profile') loader = loadAccountProfileScreen;
  else if (pathname === '/account/security') loader = loadAccountSecurityScreen;
  else if (pathname === '/workspace') loader = loadWorkspaceScreen;
  return loader?.() ?? null;
}

/**
 * §4.4: смена экрана — перекрёстное затухание 160 мс, БЕЗ перемещений.
 * Затухание меняет только прозрачность, поэтому reduced-motion здесь снимает
 * лишь длительность (мгновенная смена). Чистая функция вынесена наружу, чтобы
 * поведение «под reduce нет анимации перемещения» проверялось тестом без jsdom.
 */
export function screenFade(reduced: boolean) {
  return {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: reduced ? 0 : 0.16, ease: 'easeOut' as const },
  };
}

function Screen({ children }: { children: ReactNode }) {
  return <div className="app-screen-fade">{children}</div>;
}

function NavigationPermission({ pathname, children }: { pathname: string; children: ReactNode }) {
  const usage = useTenantUsage();
  if (usage.isPending) return <p role="status" className="text-ink-dim">проверяем права доступа…</p>;
  const role = usage.data?.actor_role;
  if (usage.isError || role === undefined) {
    return <EmptyState title="Не удалось проверить доступ" hint="Раздел не открыт без подтверждённой роли. Повторите попытку позже." />;
  }
  if (!canOpenRoute(pathname, role)) {
    return <EmptyState title="Раздел недоступен для вашей роли" hint="Попросите владельца или администратора изменить роль в рабочем пространстве." />;
  }
  return children;
}

function guarded(element: ReactNode, permissionPath?: string) {
  return (
    <RequireAuth>
      <Suspense fallback={<p role="status" className="text-ink-dim">загружаем экран…</p>}>
        <Screen>{permissionPath ? <NavigationPermission pathname={permissionPath}>{element}</NavigationPermission> : element}</Screen>
      </Suspense>
    </RequireAuth>
  );
}

class RouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route render failed', error, info);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <section className="app-route-error" role="alert" aria-labelledby="route-error-title">
        <span aria-hidden="true">!</span>
        <div>
          <h1 id="route-error-title">Раздел не удалось показать</h1>
          <p>Данные не удалены. Вернитесь на главную или перезагрузите этот экран.</p>
          <div><Link to="/">На главную</Link><button type="button" onClick={() => window.location.reload()}>Перезагрузить</button></div>
        </div>
      </section>
    );
  }
}

export function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    void preloadAppRoute(location.pathname)?.catch(() => undefined);
  }, [location.pathname]);

  return (
    <RouteErrorBoundary key={`${location.pathname}${location.search}`}>
      <Routes location={location} key={location.pathname}>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/accept-invite" element={<AcceptInvitationScreen />} />
        <Route path="/shared/shortlist/:token" element={<Suspense fallback={<p role="status" className="text-ink-dim">загружаем подборку…</p>}><Screen><SharedShortlistScreen /></Screen></Suspense>} />
        <Route path="/" element={guarded(<StationScreen />)} />
        <Route path="/tours" element={guarded(<ToursScreen />)} />
        <Route path="/compare" element={guarded(<CompareScreen />)} />
        <Route path="/stay/:stayKey" element={guarded(<StayScreen />)} />
        <Route path="/flights" element={guarded(<FlightsScreen />)} />
        <Route path="/departures" element={guarded(<DeparturesScreen />)} />
        <Route path="/events" element={guarded(<EventsScreen />)} />
        <Route path="/market" element={guarded(<MarketScreen />)} />
        <Route path="/quality" element={guarded(<QualityScreen />)} />
        <Route path="/operators" element={guarded(<OperatorsScreen />)} />
        <Route path="/watch" element={guarded(<WatchScreen />, '/watch')} />
        <Route path="/search-profiles" element={guarded(<SearchProfilesScreen />, '/search-profiles')} />
        <Route path="/signal-rules" element={guarded(<SignalRulesScreen />, '/signal-rules')} />
        <Route path="/notifications" element={guarded(<NotificationsScreen />)} />
        <Route path="/decision-history" element={guarded(<DecisionHistoryScreen />)} />
        <Route path="/pages" element={guarded(<PagesScreen />, '/pages')} />
        <Route path="/data" element={guarded(<DataScreen />)} />
        <Route path="/account/profile" element={guarded(<AccountProfileScreen />)} />
        <Route path="/account/security" element={guarded(<AccountSecurityScreen />)} />
        <Route path="/workspace" element={guarded(<WorkspaceScreen />)} />
        <Route
          path="*"
          element={guarded(
            <EmptyState title="Такой страницы нет" hint="Проверьте адрес или вернитесь на станцию" />,
          )}
        />
      </Routes>
    </RouteErrorBoundary>
  );
}
